// ────────────────────────────────────────────────────
// src/features/admin/components/AdminDashboard.tsx
// ────────────────────────────────────────────────────
import React, { useMemo, useState } from 'react';
import Tabs from '../../../../shared/components/ui/Tabs';
import { useAuth } from '../../../../shared/auth';

import ApplicationSettings from './ApplicationSettings';
import ServiceManagement   from './ServiceManagement';
import SystemManagement    from './SystemManagement';
import RbacManagement      from './RbacManagement';
import UserManagement      from './UserManagement';

// TODO: import UserManager, Metrics, etc. when those components are ready

export default function AdminDashboard() {
  const auth = useAuth();
  const { can, perms, roles, systemTabs } = auth;

  const permList = useMemo(() => Array.from(perms.values()), [perms]);
  const has = (p: string) => permList.includes(p);
  const isSuper = roles.includes('superadmin');

  // Ability mapping (provided via /me perms)
  const ABILITIES = {
    userMgmt    : 'system.user.admin',
    rbacMgmt    : 'system.rbac.admin',
    servicesMgmt: 'system.Services.Admin', // note capital S per backend
  } as const;

  /* Build tab list based on abilities (superadmin sees all) */
  const tabLabels = useMemo(() => {
    const labels: string[] = [];
    if (can('editCashFlow')) labels.push('Application Settings');
    if (isSuper || has(ABILITIES.userMgmt)) labels.push('User Management');

    const hasRoomsTab = systemTabs?.some(t => t.key === 'rooms');
    const hasSysPanel = has('system.AdminPanel.view');
    const hasRoomPerm = has('ttlock.room.admin') || has('ttlock.room.superadmin');
    const anyMgmt = isSuper || has(ABILITIES.userMgmt) || has(ABILITIES.rbacMgmt) || has(ABILITIES.servicesMgmt) || hasSysPanel || hasRoomsTab || hasRoomPerm;
    if (anyMgmt) labels.push('System Management');

    if (isSuper || has(ABILITIES.rbacMgmt)) labels.push('RBAC Management');
    if (isSuper || has(ABILITIES.servicesMgmt)) labels.push('Services Management');
    return labels;
  }, [can, isSuper, permList, systemTabs]);

  const [activeTab, setActiveTab] = useState<string>(tabLabels[0] ?? '');
  // Ensure activeTab stays valid if permissions load later
  React.useEffect(() => {
    if (!tabLabels.length) return;
    if (!activeTab || !tabLabels.includes(activeTab)) {
      setActiveTab(tabLabels[0]);
    }
  }, [tabLabels, activeTab]);

  /* ── 3) UI ─────────────────────────────────────────────────── */
  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Admin Dashboard</h1>

      {tabLabels.length > 0 ? (
        <Tabs tabs={tabLabels} activeTab={activeTab} onTabChange={setActiveTab} />
      ) : (
        <div className="bg-neutral-900 rounded-lg p-4 text-neutral-400">
          You do not have access to any admin sections.
        </div>
      )}

      {/* Application-wide settings (Cash-Flow, etc.) */}
      {activeTab === 'Application Settings' && can('editCashFlow') && (
        <ApplicationSettings />
      )}

  {/* User management */}
  {activeTab === 'User Management' && <UserManagement />}

  {/* System placeholder */}
  {activeTab === 'System Management' && <SystemManagement />}

  {/* RBAC management (roles, abilities, mappings) */}
  {activeTab === 'RBAC Management' && <RbacManagement />}

      {/* Service / script catalogue */}
      {activeTab === 'Services Management' && (
        <section className="bg-neutral-900 rounded-lg p-4 space-y-2">
          <ServiceManagement />
        </section>
      )}
    </div>
  );
}
